/**
 * Organization Feature Flags — per-organization, server-driven flags that
 * gate UI surfaces for users in the current organization. Fetched after
 * login (and on explicit refresh) from /api/v1/organizations/settings,
 * which any authenticated user can read for their own organization.
 *
 * Current flags:
 *   emCodingAssistantEnabled  — when false, the E/M Visit Code section is
 *     hidden in the Coding Assistant and in the EHR delivery confirmation
 *     modal. Diagnostic codes are unaffected.
 *   cptCodingAssistantEnabled — when false, the Procedure CPT section is
 *     hidden in the Coding Assistant and in the EHR delivery confirmation
 *     modal, and the "Improve for coding" button is hidden on the treatment
 *     summary. Diagnostic codes are unaffected.
 *
 * Default while loading / on error: flags default to TRUE so that an endpoint
 * outage never hides functionality unexpectedly; operators must explicitly
 * turn things off via Edit Organization. The exception is a failed read in a
 * standalone organization: standalone never gets E/M or CPT (the server
 * refuses them), so the error fallback turns both off there.
 */

import { create } from 'zustand';
import { apiService } from '../services/api';
import { useOrganizationStore } from './organizationStore';

interface FeatureFlags {
  emCodingAssistantEnabled: boolean;
  cptCodingAssistantEnabled: boolean;
  enableSpeakerAutoCorrect: boolean;
}

interface OrganizationFeatureFlagsState {
  flags: FeatureFlags;
  isLoaded: boolean;
  isLoading: boolean;
  error: string | null;
  fetchFlags: () => Promise<void>;
  /** Merge flag updates in from a settings save response so UI reacts
   *  immediately without a full refetch. */
  applyFlags: (partial: Partial<FeatureFlags>) => void;
  /** Clear cached flags — call on logout so a different user re-fetches. */
  reset: () => void;
}

const DEFAULT_FLAGS: FeatureFlags = {
  emCodingAssistantEnabled: true,
  cptCodingAssistantEnabled: true,
  enableSpeakerAutoCorrect: true,
};

/**
 * The /v1/organizations/settings response carries more than the two flags
 * (operation mode, EHR integration), so we type only the fields we care
 * about here and ignore the rest.
 */
interface OrganizationSettingsResponse {
  emCodingAssistantEnabled?: boolean;
  cptCodingAssistantEnabled?: boolean;
  enableSpeakerAutoCorrect?: boolean;
}

/**
 * Flags after a failed settings read, for the organization's mode: standalone never gets E/M or
 * CPT; connected keeps the defaults.
 */
const fallbackFlags = (mode: 'standalone' | 'connected'): FeatureFlags =>
  mode === 'standalone'
    ? { ...DEFAULT_FLAGS, emCodingAssistantEnabled: false, cptCodingAssistantEnabled: false }
    : DEFAULT_FLAGS;

// In-flight fetch promise — concurrent callers await the same request so no
// caller sees `undefined`. Module-scoped so the guard survives zustand's
// create() closure.
let inFlightFetch: Promise<void> | null = null;

export const useOrganizationFeatureFlags = create<OrganizationFeatureFlagsState>((set) => ({
  flags: DEFAULT_FLAGS,
  isLoaded: false,
  isLoading: false,
  error: null,

  fetchFlags: () => {
    if (inFlightFetch) return inFlightFetch;
    set({ isLoading: true, error: null });
    inFlightFetch = (async () => {
      try {
        const res = await apiService.get<OrganizationSettingsResponse>('/v1/organizations/settings');
        set({
          flags: {
            emCodingAssistantEnabled: res?.emCodingAssistantEnabled ?? true,
            cptCodingAssistantEnabled: res?.cptCodingAssistantEnabled ?? true,
            enableSpeakerAutoCorrect: res?.enableSpeakerAutoCorrect ?? true,
          },
          isLoaded: true,
          isLoading: false,
        });
      } catch (err) {
        // Keep DEFAULT_FLAGS on error: flags stay enabled so an endpoint
        // outage never hides features the user was expecting. Operators can
        // diagnose via logs; the UI stays functional. Standalone is the
        // exception: E/M and CPT are never served there, so showing them
        // would only mount sections that 403.
        // The mode may not be known yet (capabilities loads on its own); the subscription below
        // applies the fallback again once it is.
        set({
          flags: fallbackFlags(useOrganizationStore.getState().operationMode),
          isLoaded: true,
          isLoading: false,
          error: err instanceof Error ? err.message : 'Unknown error fetching organization feature flags',
        });
      } finally {
        inFlightFetch = null;
      }
    })();
    return inFlightFetch;
  },

  applyFlags: (partial) =>
    set((state) => ({
      flags: { ...state.flags, ...partial },
    })),

  reset: () => {
    inFlightFetch = null;
    set({ flags: DEFAULT_FLAGS, isLoaded: false, isLoading: false, error: null });
  },
}));

// The settings and capabilities reads run independently. After a failed settings read, follow the
// mode capabilities reports: a connected org that started from the default 'standalone', or a
// standalone org with a persisted 'connected', ends with the flags of its real mode, whichever
// response arrives first. A successful read is never overridden.
useOrganizationStore.subscribe((state, previous) => {
  if (state.operationMode === previous.operationMode) return;
  const flagsState = useOrganizationFeatureFlags.getState();
  if (flagsState.isLoaded && flagsState.error) {
    useOrganizationFeatureFlags.setState({ flags: fallbackFlags(state.operationMode) });
  }
});

/**
 * Convenience selector hook. Returns the value of a single flag. Safe to
 * use in render paths: reads from the store synchronously and re-renders
 * on change. If the store hasn't fetched yet, the flag's DEFAULT_FLAGS
 * value is returned.
 */
export function useFeatureFlag<K extends keyof FeatureFlags>(flag: K): FeatureFlags[K] {
  return useOrganizationFeatureFlags((s) => s.flags[flag]);
}
